/**
 * CR403: Safe delete modal with reassignment
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Loader2 } from "lucide-react";
import type { MerchantCategoryRow } from "@/hooks/useMerchantCategories";

interface DeleteCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: MerchantCategoryRow;
  allCategories: MerchantCategoryRow[];
  onConfirm: (newCategoryId: string, clearOverrides: boolean) => Promise<void>;
  isDeleting?: boolean;
}

export function DeleteCategoryModal({
  open,
  onOpenChange,
  category,
  allCategories,
  onConfirm,
  isDeleting,
}: DeleteCategoryModalProps) {
  const [replacementId, setReplacementId] = useState<string>("");
  const [clearOverrides, setClearOverrides] = useState(false);

  // Filter out current category from replacement options
  const replacementOptions = allCategories.filter((c) => c.id !== category.id);

  const isInUse = category.merchants_count > 0 || category.sources_override_count > 0;
  const canDelete = !isInUse || replacementId;

  const handleConfirm = async () => {
    if (!canDelete) return;
    
    // If not in use, we still need a replacement (use first available)
    const targetId = replacementId || replacementOptions[0]?.id;
    if (!targetId) return;

    await onConfirm(targetId, clearOverrides);
  };

  // Reset state when modal opens
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setReplacementId("");
      setClearOverrides(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete "{category.name}"?
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Usage summary */}
          {isInUse && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>This category is in use:</strong>
                <ul className="mt-2 text-sm space-y-1">
                  <li>• {category.merchants_count} merchants assigned</li>
                  <li>• {category.sources_override_count} source overrides</li>
                  <li>• {category.sources_inherited_count} sources inheriting</li>
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Replacement selection */}
          {(isInUse || replacementOptions.length > 0) && (
            <div className="space-y-2">
              <Label>
                {isInUse ? "Move to category *" : "Fallback category (optional)"}
              </Label>
              <Select value={replacementId} onValueChange={setReplacementId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select replacement category..." />
                </SelectTrigger>
                <SelectContent>
                  {replacementOptions.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name} ({cat.merchants_count} merchants)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Clear overrides option */}
          {isInUse && category.sources_override_count > 0 && (
            <div className="flex items-start gap-2">
              <Checkbox
                id="clear_overrides"
                checked={clearOverrides}
                onCheckedChange={(checked) => setClearOverrides(checked === true)}
              />
              <div className="grid gap-1.5 leading-none">
                <Label htmlFor="clear_overrides" className="text-sm font-medium">
                  Clear source overrides instead of moving
                </Label>
                <p className="text-xs text-muted-foreground">
                  Sources will inherit category from their merchant instead
                </p>
              </div>
            </div>
          )}

          {replacementOptions.length === 0 && (
            <Alert>
              <AlertDescription>
                You cannot delete the last category. Create a replacement first.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canDelete || isDeleting || replacementOptions.length === 0}
          >
            {isDeleting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Delete Category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
